<template>
  <ui-dialog
    :show="isOpen"
    scrollable
    persistent
    custom-class="services-dialog"
    max-width="625"
    @input="handleConditionallyCloseDialog"
  >
    <template #header>
      <div class="services-dialog__title">
        <transition name="title-fade" mode="out-in">
          <div :key="dialogTitle">
            {{ dialogTitle }}
          </div>
        </transition>
      </div>
    </template>
    <template #default>
      <div
        class="services-dialog__container"
        :class="{
          'is-active': selectedService,
          'services-dialog__container--extended':
            selectedService && selectedService.gmb_category
        }"
      >
        <transition name="toggle">
          <keep-alive>
            <GmbServiceServiceManager
              v-if="!selectedService"
              :selected-services="selectedServices"
              :categories="categories"
              :selected-category="preselectedServicesCategory"
              class="services-dialog__content"
              @service-select="handleServiceSelect"
              @revert-delete="handleRevertDelete"
              @delete="handleDeleteService"
              @save="handleSaveServices"
              @close="handleConditionallyCloseDialog"
            />
            <GmbServiceConfig
              v-else
              ref="gbmServiceConfigRef"
              :selected-service="selectedService"
              :currency="currency"
              class="services-dialog__content"
              @update="handleSelectedServiceUpdate"
              @delete="handleDeleteService"
              @close="handleCloseConfig"
            />
          </keep-alive>
        </transition>
      </div>
    </template>
    <template #loader>
      <SectionLoader :status="isLoading" />
    </template>
  </ui-dialog>
</template>

<script>
import GmbServiceServiceManager from "./GmbServiceServiceManager.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import { DialogHelper } from "@/helpers/dialog-helper";
import gmbServicesMixin from "@/helpers/mixins/gmb-services";
import SelectedServicesManager from "@/helpers/gmbServices/selected-services-manager";
import ServicePayloadBuilder from "@/helpers/gmbServices/payload-builder";
export default {
  name: "GmbServicesDialog",
  mixins: [gmbServicesMixin],
  components: {
    SectionLoader,
    GmbServiceServiceManager,
    GmbServiceConfig: () =>
      import("@/components/FacilityDetails/GmbServices/GmbServiceConfig.vue")
  },
  props: {
    isOpen: {
      type: Boolean,
      default: true
    },
    selectedServices: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      required: true
    },
    currency: {
      type: String,
      required: true
    },
    preselectedServicesCategory: {
      type: Object
    }
  },
  data() {
    return {
      selectedService: null,
      servicesPayload: [],
      selectedServicesManager: null,
      servicePayloadManager: null,
      initialServices: JSON.parse(JSON.stringify(this.selectedServices)) // deep copy of initially selected services
    };
  },
  computed: {
    serviceTitle() {
      return this.selectedService.gmb_category
        ? this.$t("message.facilityDetails.services.editServices")
        : this.selectedService.gmb_service_type.display_name;
    },
    dialogTitle() {
      return this.selectedService
        ? this.serviceTitle
        : this.$t("message.facilityDetails.services.addServices");
    },
    isModified() {
      return this.servicesPayload.length > 0;
    }
  },
  created() {
    this.selectedServicesManager = new SelectedServicesManager(
      this.selectedServices
    );
    this.servicePayloadManager = new ServicePayloadBuilder(
      this.servicesPayload
    );
  },
  methods: {
    updateServicesData(payload) {
      this.servicePayloadManager.updateServicePayload(payload);
      this.selectedServicesManager.updateServiceItems(payload);
    },
    handleSelectedServiceUpdate(payload) {
      this.updateServicesData(payload);
      this.handleCloseConfig();
    },
    handleRevertDelete(payload) {
      this.updateServicesData(payload);
    },
    async handleSaveServices(payload) {
      await this.handleServiceUpdate(payload);
      this.handleCloseDialog();
    },

    handleDeleteService(payload) {
      this.handleSelectedServiceUpdate(payload);
      this.handleCloseConfig();
    },

    async handleServiceUpdate(payload) {
      const serviceItems = {
        service_items: [...payload, ...this.servicesPayload]
      };
      this.detectChangesAndSendEvent(serviceItems, this.initialServices);
      await this.patchFacilityService(serviceItems);
    },
    handleServiceSelect(service) {
      this.selectedService = service;
    },
    handleConditionallyCloseDialog() {
      if (!this.isModified) {
        return this.handleCloseDialog();
      }
      DialogHelper.showConfirmDialog(
        {
          text: this.$t(
            "message.facilityDetails.services.closeServiceDialogText"
          ),
          okLabelKey: "message.common.yes",
          condensed: true
        },
        () => {
          this.handleCloseDialog();
        }
      );
    },
    handleCloseDialog() {
      this.$emit("close");
    },
    handleCloseConfig() {
      this.selectedService = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.toggle-enter-active,
.toggle-leave-active {
  transition: opacity 0.3s;
}
.toggle-enter, .toggle-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}

.title-fade-enter-active,
.title-fade-leave-active {
  transition: opacity 0.3s;
}

.title-fade-enter,
.title-fade-leave-to {
  opacity: 0;
}

.services-dialog {
  &__container {
    position: relative;
    max-width: 100%;
    overflow: hidden;
    display: flex;
    max-height: 650px;
    transition: max-height 0.3s 0.2s;
    &.is-active {
      transition: max-height 0.3s 0.05s;
    }
    &--extended {
      &.is-active {
        max-height: 384px;
      }
    }
    &:not(&--extended) {
      &.is-active {
        max-height: 300px;
      }
    }
  }

  &__title {
    @include font-size(22px);
    font-weight: 600;
    line-height: 26px;
    height: 30px;
    display: flex;
    align-items: center;

    div {
      position: absolute;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      max-width: calc(100% - 110px);
      display: block;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing * 3;
    width: 100%;
    flex-shrink: 0;
    max-width: 100%;
    position: relative;
    height: 100%;
  }

  &__actions {
    display: flex;
    gap: $spacing;
    justify-content: center;
    flex-grow: 1;
    margin-bottom: $spacing * 3;
  }

  &__confirm-button {
    flex-grow: 1;
  }
}
</style>
